import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Wrapper for lazy() that handles chunk loading errors gracefully
 * This prevents the ugly default error screen when dynamic imports fail
 * 
 * When a chunk loading error occurs, it will be caught by the ErrorBoundary
 * and displayed using the ErrorPage component, which provides a user-friendly
 * interface with retry functionality.
 */
export function lazyWithErrorHandling<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => {
    return importFn().catch((error) => {
      // Check if it's a chunk loading error
      const isChunkError =
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.name === 'ChunkLoadError';

      if (isChunkError) {
        // Create a custom error that will be caught by ErrorBoundary
        // The ErrorPage component will handle displaying a user-friendly message
        // and provide a retry button
        const chunkError = new Error('Failed to fetch dynamically imported module');
        chunkError.name = 'ChunkLoadError';
        // Preserve original error message if available
        if (error?.message) {
          chunkError.message = error.message;
        }
        throw chunkError;
      }

      // Re-throw other errors as-is
      throw error;
    });
  });
}

